import { useState } from "react";
import { AlertCircle, BellRing, Loader2, PlayCircle, Save } from "lucide-react";
import { toast } from "sonner";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { useSeoAlertSettings } from "@/hooks/useSeoAlertSettings";

const formatWhen = (value: string | null) =>
  value ? new Date(value).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }) : "Never";

export function SeoAlertSettingsCard() {
  const { settings, loading, saving, running, error, save, runCheckNow } = useSeoAlertSettings();
  const [draft, setDraft] = useState<{
    impressions_threshold: string;
    impressions_window_days: string;
    cooldown_hours: string;
    extra_recipients: string;
  } | null>(null);

  const form =
    draft ??
    (settings
      ? {
          impressions_threshold: String(settings.impressions_threshold),
          impressions_window_days: String(settings.impressions_window_days),
          cooldown_hours: String(settings.cooldown_hours),
          extra_recipients: settings.extra_recipients.join(", "),
        }
      : null);

  const patch = (key: keyof NonNullable<typeof form>, value: string) =>
    form && setDraft({ ...form, [key]: value });

  const handleSave = async () => {
    if (!form) return;
    const threshold = Number(form.impressions_threshold);
    const windowDays = Number(form.impressions_window_days);
    const cooldown = Number(form.cooldown_hours);
    if (!Number.isInteger(threshold) || threshold < 0) {
      toast.error("Impressions threshold must be a whole number of 0 or more.");
      return;
    }
    if (!Number.isInteger(windowDays) || windowDays < 1 || windowDays > 90) {
      toast.error("Window must be between 1 and 90 days.");
      return;
    }
    if (!Number.isInteger(cooldown) || cooldown < 1 || cooldown > 720) {
      toast.error("Cooldown must be between 1 and 720 hours.");
      return;
    }
    const recipients = form.extra_recipients
      .split(",")
      .map((v) => v.trim())
      .filter(Boolean);
    if (recipients.some((r) => !r.includes("@"))) {
      toast.error("Extra recipients must be valid email addresses.");
      return;
    }
    const ok = await save({
      impressions_threshold: threshold,
      impressions_window_days: windowDays,
      cooldown_hours: cooldown,
      extra_recipients: recipients,
    });
    if (ok) {
      setDraft(null);
      toast.success("Alert thresholds saved");
    } else {
      toast.error("Could not save alert settings");
    }
  };

  const handleToggle = async (key: "alerts_enabled" | "alert_on_indexing" | "alert_on_sitemap", value: boolean) => {
    const ok = await save({ [key]: value });
    if (!ok) toast.error("Could not update alert settings");
  };

  const handleRun = async () => {
    const result = await runCheckNow();
    if (!result) {
      toast.error("Check failed — see the error details below");
      return;
    }
    if (result.alerted) {
      toast.success(`Alert email sent for ${result.issues?.length ?? 0} issue(s)`);
    } else {
      toast.success(result.reason ? `No alert sent (${result.reason})` : "No issues detected");
    }
  };

  return (
    <Card className="border-border">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <BellRing className="h-4 w-4 text-primary" /> Search visibility alerts
        </CardTitle>
        <CardDescription>
          Email every admin when the homepage stops being indexed, the sitemap breaks or goes stale,
          or impressions fall below your threshold. Checked automatically once a day.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Alert settings error</AlertTitle>
            <AlertDescription className="break-words">{error}</AlertDescription>
          </Alert>
        )}

        {loading || !form || !settings ? (
          <div className="space-y-3">
            <Skeleton className="h-5 w-48" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : (
          <>
            <div className="space-y-3">
              {(
                [
                  ["alerts_enabled", "Alerts enabled", "Master switch for the daily check."],
                  ["alert_on_indexing", "Indexing status", "Alert when Google's verdict is not PASS."],
                  ["alert_on_sitemap", "Sitemap submission", "Alert on sitemap errors, removal, or stale reads."],
                ] as const
              ).map(([key, label, description]) => (
                <div key={key} className="flex items-start justify-between gap-4 rounded-lg border border-border p-3">
                  <div className="space-y-0.5">
                    <p className="text-sm font-medium text-foreground">{label}</p>
                    <p className="text-xs text-muted-foreground">{description}</p>
                  </div>
                  <Switch
                    checked={settings[key]}
                    disabled={saving}
                    onCheckedChange={(value) => handleToggle(key, value)}
                    aria-label={label}
                  />
                </div>
              ))}
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-1.5">
                <Label htmlFor="seo-threshold">Impressions threshold</Label>
                <Input
                  id="seo-threshold"
                  inputMode="numeric"
                  value={form.impressions_threshold}
                  onChange={(e) => patch("impressions_threshold", e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="seo-window">Window (days)</Label>
                <Input
                  id="seo-window"
                  inputMode="numeric"
                  value={form.impressions_window_days}
                  onChange={(e) => patch("impressions_window_days", e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="seo-cooldown">Cooldown (hours)</Label>
                <Input
                  id="seo-cooldown"
                  inputMode="numeric"
                  value={form.cooldown_hours}
                  onChange={(e) => patch("cooldown_hours", e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="seo-recipients">Extra recipients (comma separated)</Label>
              <Input
                id="seo-recipients"
                placeholder="growth@elarahealth.life, founder@elarahealth.life"
                value={form.extra_recipients}
                onChange={(e) => patch("extra_recipients", e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Admins and super admins always receive these alerts.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <Button onClick={handleSave} disabled={saving || !draft} className="gap-2">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Save thresholds
              </Button>
              <Button variant="outline" onClick={handleRun} disabled={running} className="gap-2">
                {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-4 w-4" />}
                Run check now
              </Button>
              <p className="text-xs text-muted-foreground">
                Last checked {formatWhen(settings.last_checked_at)} · last alert{" "}
                {formatWhen(settings.last_alerted_at)}
              </p>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default SeoAlertSettingsCard;
